import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { FirebaseApp, getApp, getApps, initializeApp } from "firebase/app";
import { deleteInstallations, getInstallations } from "firebase/installations";
import { deleteToken, getMessaging, getToken, isSupported, Messaging, onMessage } from "firebase/messaging";
import { Observable } from "rxjs";
import { firebaseConfig, firebaseVapidKey } from "../../firebase.config";
import { HttpResponse } from "../interfaces";
import { UserHttpService } from "./user-session.service";

const SERVICE_WORKER_PATH = "/firebase-messaging-sw.js";

@Injectable({
    providedIn: "root"
})
export class FirebaseMessagingService {
    private messaging: Messaging | null = null;
    private registered: boolean = false;
    private warnedMissingKey: boolean = false;

    constructor(
        private http: HttpClient,
        private userSessionService: UserHttpService
    ) { }

    /**
     * Asks for notification permission, registers the device with FCM and stores the
     * token against the logged in user. Safe to call on every page load - the browser
     * returns the same token until it is rotated or revoked.
     */
    public async register(): Promise<string | null> {
        if (this.registered) {
            return null;
        }

        // Nothing can be registered without a web push key, and this runs on every
        // navigation, so say it once instead of on each pass.
        if (!firebaseVapidKey) {
            if (!this.warnedMissingKey) {
                this.warnedMissingKey = true;
                console.log("Firebase web push key is not configured");
            }
            return null;
        }

        const messaging = await this.getMessaging();
        if (!messaging) {
            return null;
        }

        const token = await this.requestPermission();
        if (!token) {
            return null;
        }

        this.registered = true;
        this.saveFcmToken(token).subscribe({
            next: ((res) => console.log("FCM token saved", res)),
            error: ((err) => console.log("Unable to save FCM token", err))
        });
        this.listenForMessages();
        return token;
    }

    public async requestPermission(): Promise<string | null> {
        try {
            const messaging = await this.getMessaging();
            if (!messaging) {
                return null;
            }

            if (!firebaseVapidKey) {
                console.log("Firebase web push key is not configured");
                return null;
            }

            const permission = await Notification.requestPermission();
            if (permission !== "granted") {
                console.log("Notification permission denied");
                return null;
            }

            const token = await this.fetchToken(messaging);

            console.log("FCM Token:", token);

            return token;
        } catch (error) {
            console.error("FCM token error:", error);
            return null;
        }
    }

    /**
     * A Firebase installation cached in IndexedDB can go stale - for example after the
     * browser was used against a different Firebase project - and FCM then rejects the
     * subscription with "token-subscribe-failed". Dropping the installation and asking
     * once more gets a fresh credential without the user clearing site data by hand.
     */
    private async fetchToken(messaging: Messaging): Promise<string> {
        const options = {
            vapidKey: firebaseVapidKey,
            serviceWorkerRegistration: await this.getServiceWorker()
        };

        try {
            return await getToken(messaging, options);
        } catch (error) {
            if (!this.isSubscribeFailure(error)) {
                throw error;
            }

            console.log("FCM subscribe failed, resetting the cached installation and retrying once");
            await this.resetInstallation();

            return await getToken(messaging, options);
        }
    }

    private isSubscribeFailure(error: any): boolean {
        const code = error && error.code ? String(error.code) : "";
        return code.indexOf("token-subscribe-failed") >= 0 || code.indexOf("token-subscribe-no-token") >= 0;
    }

    private async resetInstallation(): Promise<void> {
        try {
            if (this.messaging) {
                await deleteToken(this.messaging);
            }
        } catch (error) {
            console.log("Unable to drop the existing FCM token", error);
        }

        try {
            await deleteInstallations(getInstallations(this.getFirebaseApp()));
        } catch (error) {
            console.log("Unable to drop the Firebase installation", error);
        }
    }

    /** Notifications that arrive while the tab is focused are not shown by the service worker. */
    public listenForMessages() {
        if (!this.messaging) {
            return;
        }

        onMessage(this.messaging, (payload) => {
            console.log("Foreground notification:", payload);
            this.showForegroundNotification(payload);
        });
    }

    private async showForegroundNotification(payload: any): Promise<void> {
        if (Notification.permission !== "granted") {
            return;
        }

        const data = payload && payload.data ? payload.data : {};
        const note = payload && payload.notification ? payload.notification : {};
        const title = note.title || data.title || "New Message";
        const body = note.body || data.body || data.message || "";
        const url = data.url || "/user/chat";

        try {
            const registration = await this.getServiceWorker();
            if (registration) {
                await registration.showNotification(title, {
                    body: body,
                    icon: "/assets/chat.svg",
                    data: { url: url }
                });
                return;
            }
        } catch (error) {
            console.log("Unable to show notification via the service worker", error);
        }

        new Notification(title, { body: body, icon: "/assets/chat.svg" });
    }

    public saveFcmToken(fcmToken: string): Observable<HttpResponse<any>> {
        let formData = new FormData();
        formData.append("user_id", this.userSessionService.getUserInfo.user_id);
        formData.append("fcm_token", fcmToken ? fcmToken : "");
        return this.http.post<HttpResponse<any>>('chat/SaveFcmToken', formData);
    }

    /**
     * Called on logout. Deleting the token unsubscribes this browser at Google, so
     * anything still sent to it is rejected as unregistered. There is no endpoint to
     * drop the row on our side yet, so nothing is posted here.
     */
    public async unregister(): Promise<void> {
        this.registered = false;

        const messaging = await this.getMessaging();
        if (!messaging) {
            return;
        }

        try {
            await deleteToken(messaging);
        } catch (error) {
            console.log("Unable to remove FCM token", error);
        }
    }

    /** Messaging needs a secure context and a browser with service worker + push support. */
    private async getMessaging(): Promise<Messaging | null> {
        if (this.messaging) {
            return this.messaging;
        }

        try {
            if (!(await isSupported())) {
                console.log("Push messaging is not supported in this browser");
                return null;
            }

            this.messaging = getMessaging(this.getFirebaseApp());
            return this.messaging;
        } catch (error) {
            console.error("Unable to start Firebase messaging:", error);
            return null;
        }
    }

    private getFirebaseApp(): FirebaseApp {
        return getApps().length > 0 ? getApp() : initializeApp(firebaseConfig);
    }

    private async getServiceWorker(): Promise<ServiceWorkerRegistration | undefined> {
        if (!("serviceWorker" in navigator)) {
            return undefined;
        }

        try {
            return await navigator.serviceWorker.register(SERVICE_WORKER_PATH);
        } catch (error) {
            console.error("Unable to register the messaging service worker:", error);
            return undefined;
        }
    }
}
